import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subject } from 'rxjs';
import {
  buildSurfacesForPageBackground,
  DEFAULT_UI_THEME,
  isDarkHexColor,
  UI_THEME_ACCENT_PRESETS,
  UI_THEME_TABLE_HEADERS,
  UsuarioUiThemeInterface
} from '../../_interfaces/ui-theme.interface';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class UiThemeService {
  private readonly apiUrl = `${environment.apiUrl}/UiTheme`;
  private currentTheme: UsuarioUiThemeInterface = { ...DEFAULT_UI_THEME };
  private saveTimer: any = null;
  private loaded = false;
  private readonly themeApplied = new Subject<UsuarioUiThemeInterface>();
  readonly themeApplied$ = this.themeApplied.asObservable();

  constructor(private http: HttpClient) {}

  getTheme(): UsuarioUiThemeInterface {
    return { ...this.currentTheme };
  }

  isLoaded(): boolean {
    return this.loaded;
  }

  loadAndApply(): Promise<UsuarioUiThemeInterface> {
    return this.http.get<UsuarioUiThemeInterface>(`${this.apiUrl}/my-theme`).toPromise()
      .then(theme => {
        this.currentTheme = this.mergeTheme(theme);
        this.loaded = true;
        this.applyToDocument(this.currentTheme);
        return this.getTheme();
      })
      .catch(() => {
        this.currentTheme = { ...DEFAULT_UI_THEME };
        this.loaded = true;
        this.applyToDocument(this.currentTheme);
        return this.getTheme();
      });
  }

  applyPageBackground(pageBackground: string, persist = true): void {
    const surfaces = buildSurfacesForPageBackground(pageBackground);
    this.updateTheme({ pageBackground, ...surfaces }, persist);
  }

  updateTheme(partial: Partial<UsuarioUiThemeInterface>, persist = true): void {
    this.currentTheme = this.mergeTheme({ ...this.currentTheme, ...partial });
    this.applyToDocument(this.currentTheme);

    if (!persist) {
      return;
    }

    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
    }

    this.saveTimer = setTimeout(() => this.persistTheme(), 450);
  }

  resetTheme(): Promise<UsuarioUiThemeInterface> {
    return this.http.delete<UsuarioUiThemeInterface>(`${this.apiUrl}/my-theme`).toPromise()
      .then(theme => {
        this.currentTheme = this.mergeTheme(theme);
        this.applyToDocument(this.currentTheme);
        return this.getTheme();
      })
      .catch(() => {
        this.currentTheme = { ...DEFAULT_UI_THEME };
        this.applyToDocument(this.currentTheme);
        return this.getTheme();
      });
  }

  applyToDocument(theme: UsuarioUiThemeInterface): void {
    const body = document.body;
    if (!body) {
      return;
    }

    body.classList.add('noc-ui-theme');
    const isDark = this.isDarkTheme(theme);
    body.classList.toggle('noc-ui-theme--dark', isDark);

    const tableHeaderBg = this.resolveTableHeaderBackground(theme);
    const tableHeaderText = this.resolveTableHeaderText(theme, tableHeaderBg);

    body.style.setProperty('--noc-page-bg', theme.pageBackground);
    body.style.setProperty('--noc-card-bg', theme.cardBackground);
    body.style.setProperty('--noc-card-header-bg', theme.cardHeaderBackground);
    body.style.setProperty('--noc-surface', theme.cardBackground);
    body.style.setProperty('--noc-surface-muted', isDark ? 'rgba(255, 255, 255, 0.06)' : '#f4f5f7');
    body.style.setProperty('--noc-table-header-bg', tableHeaderBg);
    body.style.setProperty('--noc-table-header-text', tableHeaderText);
    body.style.setProperty('--noc-accent', theme.accentColor);
    body.style.setProperty('--noc-text', isDark ? '#e8eaed' : '#343a40');
    body.style.setProperty('--noc-text-muted', isDark ? '#9aa5b1' : '#6c757d');
    body.style.setProperty('--noc-border', isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)');
    body.style.setProperty('--noc-table-row-stripe', isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.02)');
    body.style.setProperty('--noc-table-row-hover', isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)');
    body.style.setProperty('--noc-surface-hover', isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)');
    body.style.setProperty('--noc-chart-surface', theme.cardBackground);
    body.style.setProperty('--noc-chart-header-bg', isDark ? theme.cardHeaderBackground : '#fafafa');
    body.style.setProperty('--noc-chart-label', isDark ? '#b0b8c4' : '#8a8a8a');
    body.style.setProperty('--noc-chart-grid', isDark ? 'rgba(255, 255, 255, 0.12)' : '#e8e8e8');

    this.applyTopbarTheme(theme);
    this.themeApplied.next(theme);
  }

  private isDarkTheme(theme: UsuarioUiThemeInterface): boolean {
    if (theme.themeMode === 'dark') {
      return true;
    }
    if (theme.themeMode === 'light') {
      return false;
    }
    return this.isDarkColor(theme.pageBackground);
  }

  private isDarkColor(color: string): boolean {
    const rgb = this.parseHexColor(color);
    if (!rgb) {
      return false;
    }
    const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
    return luminance < 0.45;
  }

  private parseHexColor(hex: string): { r: number; g: number; b: number } | null {
    if (!hex || !hex.startsWith('#')) {
      return null;
    }
    const raw = hex.slice(1);
    if (raw.length === 3) {
      return {
        r: parseInt(raw[0] + raw[0], 16),
        g: parseInt(raw[1] + raw[1], 16),
        b: parseInt(raw[2] + raw[2], 16)
      };
    }
    if (raw.length !== 6) {
      return null;
    }
    return {
      r: parseInt(raw.slice(0, 2), 16),
      g: parseInt(raw.slice(2, 4), 16),
      b: parseInt(raw.slice(4, 6), 16)
    };
  }

  private applyTopbarTheme(theme: UsuarioUiThemeInterface): void {
    const navbars = document.querySelectorAll('.navbar');
    navbars.forEach(nav => {
      const el = nav as HTMLElement;
      el.classList.remove('bg-dark', 'bg-white', 'navbar-dark', 'navbar-light');
      el.style.removeProperty('background-color');
      if (theme.sidebarBackground === 'brown' || theme.sidebarBackground === 'black') {
        el.classList.add('bg-dark', 'navbar-dark');
        if (theme.sidebarBackground === 'black') {
          el.style.backgroundColor = '#000000';
        }
      } else {
        el.classList.add('bg-white', 'navbar-light');
      }
    });

    document.querySelectorAll('.navbar .nav-link.font-weight-semibold').forEach(link => {
      (link as HTMLElement).style.setProperty('--noc-link-accent', theme.accentColor);
    });
  }

  private resolveTableHeaderBackground(theme: UsuarioUiThemeInterface): string {
    if (theme.tableHeaderBackground === 'accent') {
      return theme.accentColor;
    }
    return theme.tableHeaderBackground;
  }

  private resolveTableHeaderText(theme: UsuarioUiThemeInterface, headerBg: string): string {
    if (theme.tableHeaderText && theme.tableHeaderText !== '#343a40') {
      return theme.tableHeaderText;
    }

    const preset = UI_THEME_TABLE_HEADERS.find(x => x.value === theme.tableHeaderBackground);
    if (preset?.cssClass === 'text-white') {
      return '#ffffff';
    }

    if (headerBg === theme.accentColor) {
      return '#ffffff';
    }

    return theme.tableHeaderText || '#343a40';
  }

  private persistTheme(): void {
    this.http.put<UsuarioUiThemeInterface>(`${this.apiUrl}/my-theme`, this.currentTheme).toPromise()
      .then(saved => {
        if (saved) {
          this.currentTheme = this.mergeTheme(saved);
        }
      })
      .catch(() => { /* silencioso — tema local já aplicado */ });
  }

  private mergeTheme(theme?: Partial<UsuarioUiThemeInterface> | null): UsuarioUiThemeInterface {
    const base = { ...DEFAULT_UI_THEME };
    if (!theme) {
      return base;
    }

    const accent = theme.accentColor || this.resolveAccentFromPreset(theme.sidebarActiveColor) || base.accentColor;

    const merged: UsuarioUiThemeInterface = {
      pageBackground: theme.pageBackground || base.pageBackground,
      cardBackground: theme.cardBackground || base.cardBackground,
      cardHeaderBackground: theme.cardHeaderBackground || base.cardHeaderBackground,
      tableHeaderBackground: theme.tableHeaderBackground || base.tableHeaderBackground,
      tableHeaderText: theme.tableHeaderText || base.tableHeaderText,
      accentColor: accent,
      sidebarBackground: theme.sidebarBackground || base.sidebarBackground,
      sidebarActiveColor: theme.sidebarActiveColor || base.sidebarActiveColor,
      themeMode: theme.themeMode === 'dark' || theme.themeMode === 'light'
        ? theme.themeMode
        : (this.isDarkColor(theme.pageBackground || base.pageBackground) ? 'dark' : 'light')
    };

    const pageDark = isDarkHexColor(merged.pageBackground);
    const cardDark = isDarkHexColor(merged.cardBackground);
    if (pageDark !== cardDark) {
      return { ...merged, ...buildSurfacesForPageBackground(merged.pageBackground) };
    }

    return merged;
  }

  private resolveAccentFromPreset(preset: string): string | null {
    const map: Record<string, string> = {
      primary: UI_THEME_ACCENT_PRESETS[0].value,
      info: UI_THEME_ACCENT_PRESETS[1].value,
      success: UI_THEME_ACCENT_PRESETS[2].value,
      warning: UI_THEME_ACCENT_PRESETS[3].value,
      danger: UI_THEME_ACCENT_PRESETS[4].value
    };
    return preset ? map[preset] || null : null;
  }
}
